import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Clock, Crosshair, Loader2, MapPin, Phone, Search, ShieldCheck, Store, Truck } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import portalApi from '../lib/portalApi';
import { destinationPath } from '../lib/portalPaths';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ACCRA = [5.6037, -0.187];

const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const pin = (open) => L.divIcon({
  className: 'portal-map-pin',
  html: `<span style="display:block;width:18px;height:18px;border-radius:9999px;border:3px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,.4);background:${open ? '#059669' : '#64748b'}"></span>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

const DirectoryMap = ({ pharmacies, me }) => {
  const holder = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);

  useEffect(() => {
    if (map.current || !holder.current) return;
    map.current = L.map(holder.current, { scrollWheelZoom: false }).setView(ACCRA, 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
  }, []);

  useEffect(() => {
    if (!map.current || !layer.current) return;
    layer.current.clearLayers();
    const points = [];
    const circles = [];
    pharmacies.forEach(p => (p.branches || []).forEach(b => {
      if (!b.latitude || !b.longitude) return;
      points.push([b.latitude, b.longitude]);
      if (p.delivers && p.delivery_radius_km) {
        L.circle([b.latitude, b.longitude], {
          radius: p.delivery_radius_km * 1000, color: '#059669', weight: 1, fillColor: '#10b981',
          fillOpacity: b.delivers_to_you ? 0.18 : 0.08, interactive: false,
        }).addTo(layer.current);
        circles.push([b.latitude, b.longitude, p.delivery_radius_km]);
      }
      L.marker([b.latitude, b.longitude], { icon: pin(b.open_now), title: b.name })
        .bindPopup(`<b>${escapeHtml(p.name)}</b><br>${escapeHtml(b.name)}<br>${escapeHtml(b.town)} ${escapeHtml(b.address)}<br>${escapeHtml(b.hours_label)}` +
          (p.delivers && p.on_time_delivery_pct != null
            ? `<br><span style="color:#92400e">${p.on_time_delivery_pct}% on-time deliveries</span>` : ''))
        .addTo(layer.current);
    }));
    if (me) {
      points.push(me);
      L.circleMarker(me, { radius: 7, color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 1 })
        .bindPopup('You are here').addTo(layer.current);
    }
    if (points.length) {
      const bounds = L.latLngBounds(points);
      // Include whole delivery circles so patients can see where coverage ends.
      circles.forEach(([lat, lng, km]) => bounds.extend(L.latLng(lat, lng).toBounds(km * 2000)));
      map.current.fitBounds(bounds.pad(0.15), { maxZoom: 14 });
    }
  }, [pharmacies, me]);

  return <div ref={holder} className="h-64 sm:h-80 w-full rounded-xl border overflow-hidden z-0"
    data-testid="portal-directory-map" />;
};

const PharmacyServices = ({ pharmacy: p }) => {
  if (!p.delivers && !(p.insurers || []).length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`find-services-${p.tenant_id}`}>
      {p.delivers_to_you && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2 py-0.5 text-xs font-medium"
          data-testid={`find-delivers-to-you-${p.tenant_id}`}>
          <Truck className="w-3 h-3" /> Delivers to you
        </span>
      )}
      {p.delivers && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-xs"
          data-testid={`find-delivers-${p.tenant_id}`}>
          <Truck className="w-3 h-3" /> Delivers{p.delivery_radius_km ? ` within ${p.delivery_radius_km} km` : ''}{p.delivery_note ? ` · ${p.delivery_note}` : ''}
        </span>
      )}
      {p.delivers && p.on_time_delivery_pct != null && (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
          p.on_time_delivery_pct >= 90 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}
          data-testid={`find-on-time-${p.tenant_id}`} title="Share of deliveries completed on time over the last 30 days">
          <BadgeCheck className="w-3 h-3 shrink-0" /> {p.on_time_delivery_pct}% on-time deliveries
        </span>
      )}
      {(p.insurers || []).length > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 text-xs"
          data-testid={`find-insurers-${p.tenant_id}`}>
          <ShieldCheck className="w-3 h-3 shrink-0" /> Accepts {p.insurers.join(', ')}
        </span>
      )}
    </div>
  );
};

export default function PortalFindPage() {
  const [query, setQuery] = useState('');
  const [town, setTown] = useState('');
  const [openNow, setOpenNow] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [insurer, setInsurer] = useState('');
  const [me, setMe] = useState(null);
  const [locating, setLocating] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const params = { q: query || undefined, town: town || undefined, open_now: openNow || undefined,
      delivery: delivery || undefined, insurer: insurer || undefined };
    if (me) { params.lat = me[0]; params.lng = me[1]; }
    portalApi.get('/directory', { params })
      .then(r => { setData(r.data); setError(''); })
      .catch(() => setError('The pharmacy directory could not be loaded. Please try again.'));
  }, [query, town, openNow, delivery, insurer, me]);

  useEffect(() => {
    const timer = setTimeout(load, query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  const locate = () => {
    if (!navigator.geolocation) return setError('This device cannot share its location — search by town instead.');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setMe([pos.coords.latitude, pos.coords.longitude]); setLocating(false); },
      () => { setLocating(false); setError('We could not get your location — search by town instead.'); },
      { timeout: 10000 },
    );
  };

  const pharmacies = useMemo(() => data?.pharmacies || [], [data]);

  return (
    <main className="min-h-screen bg-slate-50" data-testid="portal-find-page">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-5 space-y-1">
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Store className="w-5 h-5 text-emerald-600" /> Find a pharmacy
          </h1>
          <p className="text-sm text-slate-600">
            Every pharmacy here is verified by DutiLyne and open for business. Search your town, or let us
            find the closest counter.
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="h-12 pl-9" placeholder="Town, pharmacy or street"
              value={query} onChange={e => setQuery(e.target.value)} data-testid="find-search-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant={me ? 'default' : 'outline'} className="h-10 gap-2"
              onClick={locate} disabled={locating} data-testid="find-use-location">
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
              {me ? 'Sorted by nearest' : 'Use my location'}
            </Button>
            <Button type="button" variant={openNow ? 'default' : 'outline'} className="h-10 gap-2"
              onClick={() => setOpenNow(v => !v)} data-testid="find-open-now">
              <Clock className="w-4 h-4" /> Open now
            </Button>
            <Button type="button" variant={delivery ? 'default' : 'outline'} className="h-10 gap-2"
              onClick={() => setDelivery(v => !v)} aria-pressed={delivery} data-testid="find-delivery-filter">
              <Truck className="w-4 h-4" /> {me ? 'Delivers to me' : 'Delivers'}
            </Button>
            {(data?.insurers || []).length > 0 && (
              <select className="h-10 rounded-md border bg-white px-2 text-sm max-w-[14rem]" value={insurer}
                onChange={e => setInsurer(e.target.value)} aria-label="Insurance accepted" data-testid="find-insurer-filter">
                <option value="">Any insurance</option>
                {data.insurers.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
            {(data?.towns || []).length > 1 && (
              <select className="h-10 rounded-md border bg-white px-2 text-sm" value={town}
                onChange={e => setTown(e.target.value)} data-testid="find-town-filter">
                <option value="">All towns</option>
                {data.towns.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </div>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="find-error">{error}</p>}
        </div>

        <DirectoryMap pharmacies={pharmacies} me={me} />

        {!data && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}
        {data && pharmacies.length === 0 && (
          <p className="text-sm text-slate-600 bg-white border rounded-xl p-4" data-testid="find-empty">
            Nothing matches that yet. Try a nearby town, or switch off a filter like “Open now”, “Delivers” or insurance.
          </p>
        )}

        <div className="space-y-4">
          {pharmacies.map(p => (
            <section className="bg-white border rounded-xl overflow-hidden" key={p.tenant_id}
              data-testid={`find-pharmacy-${p.tenant_id}`}>
              <div className="flex items-center gap-3 p-4 border-b">
                {p.logo_url
                  ? <img src={p.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover border"
                      onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'grid'; }} />
                  : null}
                <span className="w-10 h-10 rounded-lg place-items-center text-white font-semibold shrink-0"
                  style={{ background: p.primary_color, display: p.logo_url ? 'none' : 'grid' }}>{p.name.slice(0, 1)}</span>
                <div className="min-w-0">
                  <h2 className="font-semibold text-slate-900 truncate">{p.name}</h2>
                  <p className="text-xs text-slate-600">
                    {p.branch_count} location{p.branch_count === 1 ? '' : 's'}
                    {p.nearest_km != null ? ` · nearest ${p.nearest_km} km` : ''}
                    {p.any_open_now ? ' · open now' : ''}
                  </p>
                  <PharmacyServices pharmacy={p} />
                </div>
              </div>
              <ul className="divide-y">
                {p.branches.map(b => (
                  <li className="p-4 flex flex-wrap items-start justify-between gap-3"
                    key={b.tenant_id} data-testid={`find-branch-${b.tenant_id}`}>
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium text-slate-900">{b.name}</p>
                      <p className="text-xs text-slate-600 flex items-center gap-1">
                        <MapPin className="w-3 h-3 shrink-0" />
                        {[b.town, b.address].filter(Boolean).join(' · ') || 'Address on request'}
                        {b.distance_km != null ? ` · ${b.distance_km} km away` : ''}
                      </p>
                      <p className={`text-xs flex items-center gap-1 ${b.open_now ? 'text-emerald-700' : 'text-slate-500'}`}
                        data-testid={`find-hours-${b.tenant_id}`}>
                        <Clock className="w-3 h-3" /> {b.hours_label}
                      </p>
                      {b.phone && (
                        <a className="text-xs text-slate-700 flex items-center gap-1 underline" href={`tel:${b.phone}`}>
                          <Phone className="w-3 h-3" /> {b.phone}
                        </a>
                      )}
                    </div>
                    <Link to={destinationPath({ tenant_id: p.tenant_id, slug: p.slug },
                      { tenant_id: b.tenant_id, slug: b.slug })}
                      data-testid={`find-open-${b.tenant_id}`}>
                      <Button className="h-10">Open this pharmacy</Button>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="text-xs text-slate-500 pb-8">
          Closer to your patients, Connected to your Pharmacy.
        </p>
      </div>
    </main>
  );
}
